import { debugFetch } from '../utils/apiDebug.js';
import { t } from '../language/pageTranslator.js';
import { initializeTrainingModeRuntime } from '../training/trainingMode.js';

initializeTrainingModeRuntime();

class ManagerAPI {
    static getAuthParams() {
        const managerID = localStorage.getItem('managerID');
        const token = localStorage.getItem('managerToken');

        if (!managerID || !token) {
            throw new Error(t('apiError.sessionExpired'));
        }

        return { managerID, token };
    }

    buildFormData(fields) {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value !== undefined && value !== null ? value : '');
        });
        return formData;
    }

    async postManagerForm(endpoint, fields, options = {}) {
        const includeAuth = options.includeAuth !== false;
        const authFields = includeAuth ? ManagerAPI.getAuthParams() : {};
        const body = this.buildFormData({
            ...authFields,
            ...fields
        });

        try {
            const response = await debugFetch(`https://hello-zima.appspot.com/MAPIv1/${endpoint}`, {
                method: 'POST',
                body
            });

            if (!response.ok) {
                throw new Error(`${endpoint} failed: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`${endpoint} API error:`, error);
            throw error;
        }
    }

    async createManager({ email, auth, name } = {}) {
        return this.postManagerForm('createManager', {
            email,
            auth,
            name
        });
    }

    async allCities() {
        return this.postManagerForm('allCities', {});
    }

    async managers() {
        return this.postManagerForm('managers', {});
    }

    async createCity({
        countryID,
        cityName,
        districts,
        timezoneName,
        price,
        support,
        gateway,
        available,
        mainLang
    } = {}) {
        return this.postManagerForm('createCity', {
            countryID,
            cityName,
            districts,
            timezoneName,
            price,
            support,
            gateway,
            available,
            mainLang
        });
    }

    async updateCity({
        cityID,
        cityName,
        districts,
        timezoneName,
        price,
        support,
        gateway,
        presale,
        available,
        mainLang
    } = {}) {
        return this.postManagerForm('updateCity', {
            cityID,
            cityName,
            districts,
            timezoneName,
            price,
            support,
            gateway,
            presale,
            available,
            mainLang
        });
    }

    async cityManagers(cityID) {
        return this.postManagerForm('cityManagers', {
            cityID
        });
    }

    async assignManagerToCity({ assigningManagerID, cityID, startDate } = {}) {
        return this.postManagerForm('assignManagerToCity', {
            assigningManagerID,
            cityID,
            startDate
        });
    }

    async revokeManager(revokeManagerID) {
        return this.postManagerForm('revokeManager', {
            revokeManagerID
        });
    }

    async resetManagerPassword(resetManagerID) {
        return this.postManagerForm('resetManagerPassword', {
            resetManagerID
        });
    }

    async updateManagerPassword(managerID, token, oldPassword, newPassword) {
        if (!managerID || !token) {
            throw new Error(t('apiError.sessionExpired'));
        }

        if (!oldPassword || !newPassword) {
            throw new Error('Missing password parameters.');
        }

        try {
            const params = new URLSearchParams();
            params.append('managerID', managerID);
            params.append('token', token);
            params.append('oldPassword', oldPassword);
            params.append('newPassword', newPassword);

            const response = await debugFetch('https://hello-zima.appspot.com/MAPIv1/updateManagerPassword', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: params
            });

            if (!response.ok) {
                throw new Error(`Update manager password failed: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Update manager password API error:', error);
            throw error;
        }
    }
}

const managerApi = new ManagerAPI();
export default managerApi;
